
import { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import api from "../Axios/api";
import Loader from "../common/Loader";
import Swal from "sweetalert2";
import { Eye, Pencil, Trash2 } from "lucide-react";
const ConsumptionRecord = () => {
    const navigate = useNavigate();

    const [vouchers, setVouchers] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedFactory, setSelectedFactory] = useState("all");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [month, setMonth] = useState("");

    // Fetch Issue Voucher Records
    const fetchIssueVouchers = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await api.get(`/inventory/issueRecord?month=${month}`);

            setVouchers(response.data?.data || []);
        } catch (err) {
            console.error("Error fetching issue records:", err);

            setError(
                err.response?.data?.message ||
                "Failed to load issue vouchers."
            );
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (month) {
            fetchIssueVouchers();
        }
    }, [month]);

    useEffect(() => {
        const now = new Date();
        const year = now.getFullYear();
        const monthNum = String(now.getMonth() + 1).padStart(2, "0");
        const currentMonth = `${year}-${monthNum}`;
        setMonth(currentMonth);
    }, []);
    const formatDate = (date) => {
        if (!date) return "-";

        const parsedDate = new Date(date);

        if (Number.isNaN(parsedDate.getTime())) return "-";

        return parsedDate.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            timeZone: "UTC",
        });
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
        }).format(Number(amount || 0));
    };

    const factories = useMemo(() => {
        return [
            ...new Set(
                vouchers
                    .map((item) => item.factoryName)
                    .filter(Boolean)
            ),
        ];
    }, [vouchers]);

    const filteredVouchers = useMemo(() => {
        const searchText = search.trim().toLowerCase();

        return vouchers.filter((item) => {
            const matchesSearch = [
                item.IssueVoucherNo,
                item.IssueTo,
                item.factoryName,
                item.IssueTag,
            ].some((value) =>
                String(value || "")
                    .toLowerCase()
                    .includes(searchText)
            );

            const matchesFactory =
                selectedFactory === "all" ||
                item.factoryName === selectedFactory;

            return matchesSearch && matchesFactory;
        });
    }, [vouchers, search, selectedFactory]);

    // View Voucher
    const handleView = (row) => {
        const id = row;
        navigate(`/add-consumption`,
            {

                state: id
            }
        )
    };
    const handleDelete = async (row) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "Do you really want to delete ?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            try {
                const response = await api.delete(`/inventory/issueDelete/${row}`);
                if (response) {
                    Swal.fire({
                        title: "Deleted!",
                        text: "Data deleted successfully!",
                        icon: "success",
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    fetchIssueVouchers();
                }
            } catch (error) {
                console.error("Error deleting data:", error);
                Swal.fire({
                    title: "Error!",
                    text: "Something went wrong while deleting the data.",
                    icon: "error",
                });
            }
        }
    };
    const columns = [
        {
            name: "S.No.",
            cell: (row, index) => index + 1,
            width: "80px",
        },
        {
            name: "Voucher No.",
            selector: (row) => row.IssueVoucherNo || "-",
            sortable: true,
            minWidth: "160px",
            cell: (row) => (
                <span className="font-semibold text-blue-700">
                    {row.IssueVoucherNo || "-"}
                </span>
            ),
        },
        {
            name: "Issue Date",
            selector: (row) => row.IssueDate || "",
            sortable: true,
            minWidth: "130px",
            cell: (row) => formatDate(row.IssueDate),
        },
        {
            name: "Factory",
            selector: (row) => row.factoryName || "-",
            sortable: true,
            minWidth: "200px",
            wrap: true,
            cell: (row) => (
                <span className="rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700">
                    {row.factoryName || "-"}
                </span>
            ),
        },
        {
            name: "Issue To",
            selector: (row) => row.IssueTo || "-",
            sortable: true,
            minWidth: "140px",
            wrap: true,
        },
        {
            name: "Issue Tag",
            selector: (row) => row.IssueTag || "-",
            sortable: true,
            cell: (row) => (
                <span className="rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
                    {row.IssueTag || "-"}
                </span>
            ),
        },
        {
            name: "Total Amount",
            selector: (row) => Number(row.totalAmount || 0),
            sortable: true,
            right: true,
            minWidth: "150px",
            cell: (row) => (
                <span className="font-semibold text-gray-800">
                    {formatAmount(row.totalAmount)}
                </span>
            ),
        },
        {
            name: "Action",
            width: "150px",
            center: true,
            cell: (row) => (
                <div className="flex items-center justify-center gap-2">
                    {/* View Button */}
                    <button
                        type="button"
                        onClick={() => handleView(row._id)}
                        title="View"
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-cyan-200 bg-cyan-50 text-cyan-600 transition-all duration-200 hover:border-cyan-500 hover:bg-cyan-500 hover:text-white"
                    >
                        <Eye size={16} />
                    </button>


                    {/* Delete Button */}
                    <button
                        type="button"
                        onClick={() => handleDelete(row._id)}
                        title="Delete"
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-600 transition-all duration-200 hover:border-red-500 hover:bg-red-500 hover:text-white"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            ),
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "#f9fafb",
                borderBottom: "1px solid #e5e7eb",
                minHeight: "52px",
            },
        },
        headCells: {
            style: {
                color: "#4b5563",
                fontSize: "12px",
                fontWeight: "600",
            },
        },
        rows: {
            style: {
                minHeight: "60px",
                fontSize: "13px",
                color: "#374151",
            },
            highlightOnHoverStyle: {
                backgroundColor: "#f9fafb",
                transition: "background-color 0.2s",
            },
        },
        pagination: {
            style: {
                borderTop: "1px solid #e5e7eb",
                fontSize: "13px",
            },
        },
    };


    return (
        <div className="p-6">
            <div className="mb-3 flex flex-row items-center justify-between rounded-lg bg-white p-3">
                <h1 className="mb-0 text-2xl font-bold text-gray-800">
                    Consumption Record
                </h1>

                <button
                    type="button"
                    onClick={() => navigate("/add-consumption")}
                    className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                >
                    Add Form
                </button>
            </div>

            {/* Search and Factory Filter */}

            <div className="mb-4 flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm md:flex-row md:items-center">
                {/* Search Input */}
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search voucher number, factory, or issue to..."
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 md:w-80"
                />

                {/* Factory Filter */}
                <select
                    value={selectedFactory}
                    onChange={(e) => setSelectedFactory(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 md:w-48"
                >
                    <option value="all">All Factories</option>

                    {factories.map((factory) => (
                        <option key={factory} value={factory}>
                            {factory}
                        </option>
                    ))}
                </select>
                <div className="flex flex-col gap-1">

                    <input
                        id="issue-month"
                        type="month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                </div>
            </div>


            {/* Error Message */}
            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                    {error}
                </div>
            )}

            {/* Data Table */}
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                {loading ? (
                    <Loader />
                ) : (
                    <DataTable
                        columns={columns}
                        data={filteredVouchers}
                        keyField="_id"
                        customStyles={customStyles}
                        progressPending={loading}
                        pagination
                        paginationPerPage={10}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        highlightOnHover
                        responsive
                        striped
                        persistTableHead
                        noDataComponent={
                            <div className="py-10 text-sm text-gray-500">
                                {error
                                    ? "Unable to load records."
                                    : "No consumption records found."}
                            </div>
                        }
                    />
                )}

            </div>
        </div>
    );
};

export default ConsumptionRecord;
